import { IconButton } from '@brightdrop/bd-ui';
import { useRole } from '@gm-commercial/profile-context';
import { Role } from '@gm-commercial/profile-model';
import { Popover, useMediaQuery, useTheme } from '@mui/material';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelect } from '~/app/store';
import AlertRedCircleIcon from '~/assets/icons/new/alertRedCircleIcon.svg?react';
import NotificationsIcon from '~/assets/icons/notificationsIcon.svg?react';
import { translateText as t } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { HEAP_EVENTS } from '~/common/models/heap.model';
import { FLEET_NOTIFICATIONS_LIST } from '~/common/models/pages/fleetPages.model';
import { dispatchHeapEvent } from '~/common/utils/analytics/heap.utils';
import { useBadgeAlerts } from '~/features/header/components/NotificationBadge/hooks/useBadgeAlerts';
import NotificationPopover from '~/routes/notifications/components/NotificationPopover/NotificationPopover';
import { setPopUp } from '~/store/alerts/alertsSlice';
import {
  selectAlerts,
  selectPopUp,
} from '~/store/alerts/alertsSlice.selectors';

import useStyles from './NotificationBadge.styles';

const NotificationBadge = () => {
  // Subscribe to i18n so imperative `translateText(...)` updates after locale changes.
  useTranslation(['common']);
  const theme = useTheme();
  const shouldRedirectToNotificationsCenter = useMediaQuery(
    theme.breakpoints.down('md')
  );
  const navigate = useNavigate();

  const divRef = useRef<HTMLDivElement | null>(null);
  const isPopoverOpen = useAppSelect(selectPopUp);
  const [, unreadAlerts] = useAppSelect(selectAlerts);
  const dispatch = useAppDispatch();
  const { classes } = useStyles();

  useBadgeAlerts();

  // Show unread badge based on Redux state (includes socket notifications)
  const showUnread = Boolean(unreadAlerts?.length);

  //TODO: Update to permission when the BE provides the correct permission
  const { hasRole } = useRole(false);
  const isInternalUser = hasRole([Role.SYSTEM_MANAGER, Role.SYSTEM_READER]);

  const handleClickNotification = () => {
    if (location.pathname === FLEET_NOTIFICATIONS_LIST) {
      window.location.reload();
      return;
    }

    if (shouldRedirectToNotificationsCenter) {
      navigate(FLEET_NOTIFICATIONS_LIST);
    } else {
      dispatchHeapEvent(HEAP_EVENTS.NOTIFICATION_POPOVER_VIEWED, {
        hasUnreadAlerts: showUnread ? 'true' : 'false',
      });
      dispatch(setPopUp(true));
    }
  };

  const handleCloseNotification = () => {
    dispatch(setPopUp(false));
  };

  const generateNotificationIconClassname = () => {
    const selectedClass =
      isPopoverOpen || location.pathname === FLEET_NOTIFICATIONS_LIST
        ? 'selected'
        : '';
    return `${classes.notificationIconButton} ${selectedClass}`;
  };

  const NotificationIcon = () => (
    <IconButton
      size="medium"
      data-testid="notifications-icon"
      aria-label={t('common:notifications')}
      aria-haspopup="true"
      onClick={handleClickNotification}
      className={generateNotificationIconClassname()}
      icon={<NotificationsIcon />}
    />
  );

  const UnreadNotificationIcon = () => (
    <IconButton
      size="medium"
      data-testid="notifications-icon-unread"
      aria-haspopup="true"
      aria-label={t('common:unreadNotifications')}
      onClick={handleClickNotification}
      className={generateNotificationIconClassname()}
      icon={
        <>
          <NotificationsIcon />
          <AlertRedCircleIcon className={classes.alertRedCircleIcon} />
        </>
      }
    />
  );

  let Icon = <NotificationIcon />;
  if (showUnread) {
    Icon = <UnreadNotificationIcon />;
  }

  return (
    <>
      {!isInternalUser && (
        <div ref={divRef}>
          {Icon}
          <Popover
            open={isPopoverOpen}
            anchorEl={divRef.current}
            classes={{ paper: classes.popoverContent }}
            onClose={handleCloseNotification}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <NotificationPopover onClose={handleCloseNotification} />
          </Popover>
        </div>
      )}
    </>
  );
};

export default NotificationBadge;
